"use client";

import { useRouter, useSearchParams } from "@/infrastructure/router/navigation";
import {
	type ChangeEvent,
	type ReactNode,
	useCallback,
	useEffect,
	useRef,
	useState,
} from "react";
import {
	ArtistIcon,
	ArtworkThumbnail,
	Callout,
	EmptyState,
	GlassButton,
	GlassCard,
	GlassIconButton,
	GlassTextEditor,
	GlassTextField,
	NavigationBar,
	Toast,
} from "@/components/design-system/ArtistDesignSystem";
import { useArtistSession } from "@/components/auth/ArtistSessionProvider";
import {
	loadLocalTrackAsset,
	type LocalTrackContinentAsset,
	updateLocalTrackAsset,
} from "@/domain/media/LocalTrackAssetStore";
import { offlineSocialRepository } from "@/domain/social/SocialRepository";

interface EditableTrackSource {
	readonly id: string;
	readonly title: string;
	readonly artist: string;
	readonly album: string;
	readonly durationSeconds: number;
	readonly audioFilename: string;
	readonly artworkURL: string | null;
	readonly continents: readonly ContinentDraft[];
	readonly lyricsFilename: string | null;
	readonly lyricsText: string;
}

// ─────────────────────────────────────────────────────────────────────────────
// Lyrics mode — mirrors `TrackEditView.LyricsEditMode`. 5 cases, each with a
// Chinese label, description, and matching SF Symbol → ArtistIcon name.
// ─────────────────────────────────────────────────────────────────────────────

type LyricsEditMode = "keep" | "autoDetect" | "remove" | "file" | "text";

interface LyricsModeDescriptor {
	readonly mode: LyricsEditMode;
	readonly label: string;
	readonly description: string;
	readonly icon: "check" | "sparkle" | "trash" | "doc" | "create";
	readonly message?: string;
}

const LYRICS_MODES: readonly LyricsModeDescriptor[] = [
	{
		mode: "keep",
		label: "保留",
		description: "保留当前歌词与同步设置。",
		icon: "check",
		message: "保存后歌词与同步状态保持不变。",
	},
	{
		mode: "autoDetect",
		label: "自动识别",
		description: "清除当前歌词，由后台在保存后重新识别。",
		icon: "sparkle",
		message: "保存后将清空歌词并请求后台重新转录与对齐。",
	},
	{
		mode: "remove",
		label: "移除",
		description: "从此曲目中移除全部歌词。",
		icon: "trash",
		message: "保存后歌词会被清空，且不会触发自动识别。",
	},
	{
		mode: "file",
		label: "导入文件",
		description:
			"导入 .lrc / .txt / .srt / .vtt 歌词文件，自动检测是否包含时间轴。",
		icon: "doc",
	},
	{
		mode: "text",
		label: "粘贴文本",
		description: "直接粘贴歌词，自动识别纯文本或同步格式。",
		icon: "create",
	},
] as const;

const LYRICS_MAX_CHARS = 5000;

// ─────────────────────────────────────────────────────────────────────────────
// ContinentDraft — mirrors `ContinentDraft` in iOS. `existing` keeps the
// original URL; `newImage` / `newVideo` carry an object URL for a freshly
// picked file. Identifiable by `id` so reorder / replace is stable.
// ─────────────────────────────────────────────────────────────────────────────

type ContinentMediaType = "image" | "video";

interface ContinentDraft {
	readonly id: string;
	readonly source: "existing" | "newImage" | "newVideo";
	readonly mediaType: ContinentMediaType;
	readonly url: string;
	readonly filename: string;
	readonly blob: Blob;
}

function continentFromAsset(asset: LocalTrackContinentAsset): ContinentDraft {
	return {
		id: asset.id,
		source: "existing",
		mediaType: asset.mediaType,
		url: URL.createObjectURL(asset.blob),
		filename: asset.filename,
		blob: asset.blob,
	};
}

function continentsFromAsset(
	continents: readonly LocalTrackContinentAsset[],
): ContinentDraft[] {
	return continents.map(continentFromAsset);
}

function continentsEqual(
	a: readonly ContinentDraft[],
	b: readonly ContinentDraft[],
): boolean {
	if (a.length !== b.length) return false;
	for (let index = 0; index < a.length; index += 1) {
		const left = a[index];
		const right = b[index];
		if (left === undefined || right === undefined) return false;
		if (left.id !== right.id) return false;
		if (left.source !== right.source) return false;
		if (left.mediaType !== right.mediaType) return false;
		if (left.url !== right.url) return false;
	}
	return true;
}

// ─────────────────────────────────────────────────────────────────────────────
// Lyrics parser — simplified web-side mirror of `LyricsParser.parse`. Detects
// a `[mm:ss.xx]` tag anywhere on a line to classify as "synced"; otherwise the
// first non-empty line is returned as a "plain" preview.
// ─────────────────────────────────────────────────────────────────────────────

interface LyricsDetection {
	readonly kind: "synced" | "plain";
	readonly preview: string;
}

const SYNCED_LINE_PATTERN = /\[\d{1,2}:\d{1,2}(?:[.:]\d{1,3})?\]/;

function detectLyrics(text: string): LyricsDetection | null {
	const trimmed = text.trim();
	if (trimmed.length === 0) return null;
	const firstLine = trimmed
		.split(/\r?\n/)
		.find((line) => line.trim().length > 0);
	if (firstLine === undefined) return null;
	if (SYNCED_LINE_PATTERN.test(trimmed)) {
		return {
			kind: "synced",
			preview: firstLine.replace(SYNCED_LINE_PATTERN, "").trim() || firstLine,
		};
	}
	return { kind: "plain", preview: firstLine };
}

// ─────────────────────────────────────────────────────────────────────────────
// ID generator — `crypto.randomUUID()` when available, fallback to a counter.
// ─────────────────────────────────────────────────────────────────────────────

let continentCounter = 0;
function makeContinentId(): string {
	if (
		typeof crypto !== "undefined" &&
		typeof crypto.randomUUID === "function"
	) {
		return crypto.randomUUID();
	}
	continentCounter += 1;
	return `continent-draft-${Date.now()}-${continentCounter}`;
}

// ─────────────────────────────────────────────────────────────────────────────
// Format helpers — duration as `m:ss` (mirrors `Track.formattedDuration`).
// ─────────────────────────────────────────────────────────────────────────────

function formatDuration(seconds: number): string {
	if (!Number.isFinite(seconds) || seconds <= 0) return "0:00";
	const minutes = Math.floor(seconds / 60);
	const remainder = Math.floor(seconds % 60);
	return `${minutes}:${remainder.toString().padStart(2, "0")}`;
}

export function TrackEditView() {
	const router = useRouter();
	const searchParams = useSearchParams();
	const session = useArtistSession();
	const trackId = searchParams.get("id");
	const [sourceTrack, setSourceTrack] = useState<EditableTrackSource | null>(null);
	const [trackRevision, setTrackRevision] = useState(1);
	const [loadState, setLoadState] = useState<"loading" | "ready" | "missing">(
		"loading",
	);

	// Draft state — mirrors the @State fields on iOS.
	const [draftTitle, setDraftTitle] = useState("");
	const [draftAlbumURL, setDraftAlbumURL] = useState<string | null>(null);
	const [draftAlbumFile, setDraftAlbumFile] = useState<File | null>(null);
	const [removeArtwork, setRemoveArtwork] = useState(false);
	const [draftContinents, setDraftContinents] = useState<ContinentDraft[]>([]);
	const [originalContinents, setOriginalContinents] = useState<ContinentDraft[]>([]);
	const [activeContinentIndex, setActiveContinentIndex] = useState<
		number | null
	>(null);

	const [lyricsMode, setLyricsMode] = useState<LyricsEditMode>("keep");
	const [lyricsText, setLyricsText] = useState("");
	const [lyricsFilename, setLyricsFilename] = useState<string | null>(null);
	const [lyricsDetection, setLyricsDetection] =
		useState<LyricsDetection | null>(null);

	const [isSaving, setIsSaving] = useState(false);
	const [notice, setNotice] = useState<string | null>(null);
	const [error, setError] = useState<string | null>(null);
	const [showDiscardDialog, setShowDiscardDialog] = useState(false);

	// Hidden file inputs — refs let us trigger `click()` from button handlers
	// without nesting the input inside the button (which would confuse
	// semantics on iOS Safari).
	const coverInputRef = useRef<HTMLInputElement | null>(null);
	const continentInputRef = useRef<HTMLInputElement | null>(null);
	const lyricsInputRef = useRef<HTMLInputElement | null>(null);

	// Persist a tiny draft watermark so a refresh doesn't lose work mid-edit.
	// Mirrors the iOS pattern of keeping the editor state alive across
	// navigation transitions. `draftKey` is a module-level constant string,
	// so the hook deps stay empty.
	useEffect(() => {
		let active = true;
		const objectURLs: string[] = [];
		if (session.phase === "loading") {
			setLoadState("loading");
			return () => {
				active = false;
			};
		}
		if (session.phase !== "user" || session.scope === null || trackId === null) {
			setLoadState("missing");
			return () => {
				active = false;
			};
		}
		setLoadState("loading");
		Promise.all([
			loadLocalTrackAsset(session.scope, trackId),
			offlineSocialRepository.loadTrack(session.scope, trackId),
		])
			.then(([asset, record]) => {
				if (!active || asset === null) {
					if (active) setLoadState("missing");
					return;
				}
				const artworkURL = asset.artworkBlob === null
					? null
					: URL.createObjectURL(asset.artworkBlob);
				if (artworkURL !== null) objectURLs.push(artworkURL);
				const continents = continentsFromAsset(asset.continents);
				objectURLs.push(...continents.map((continent) => continent.url));
				const title = record.value.title.trim() || asset.title;
				const initialLyricsMode: LyricsEditMode =
					asset.lyricsText.length > 0 ? "text" : "keep";
				const source: EditableTrackSource = {
					id: asset.id,
					title,
					artist: "你",
					album: "本地作品",
					durationSeconds: asset.durationSeconds,
					audioFilename: asset.audioFilename,
					artworkURL,
					continents,
					lyricsFilename: asset.lyricsFilename,
					lyricsText: asset.lyricsText,
				};
				setSourceTrack(source);
				setTrackRevision(record.revision);
				setDraftTitle(title);
				setDraftContinents(continents);
				setOriginalContinents(continents.map((item) => ({ ...item })));
				setLyricsMode(initialLyricsMode);
				setLyricsText(asset.lyricsText);
				setLyricsFilename(asset.lyricsFilename);
				setLyricsDetection(detectLyrics(asset.lyricsText));
				setDraftAlbumURL(null);
				setDraftAlbumFile(null);
				setRemoveArtwork(false);
				setLoadState("ready");
			})
			.catch(() => {
				if (active) setLoadState("missing");
			});
		return () => {
			active = false;
			objectURLs.forEach((url) => {
				URL.revokeObjectURL(url);
			});
		};
	}, [session.phase, session.scope, trackId]);

	const draftKey = trackId === null ? null : `artist.track-edit.draft.v1:${trackId}`;
	useEffect(() => {
		if (typeof window === "undefined" || draftKey === null || loadState !== "ready") return;
		try {
			const raw = window.localStorage.getItem(draftKey);
			if (raw === null) return;
			const restored = JSON.parse(raw) as {
				readonly title?: string;
				readonly lyricsMode?: LyricsEditMode;
				readonly lyricsText?: string;
				readonly lyricsFilename?: string | null;
			};
			if (typeof restored.title === "string") setDraftTitle(restored.title);
			if (typeof restored.lyricsMode === "string")
				setLyricsMode(restored.lyricsMode);
			if (typeof restored.lyricsText === "string")
				setLyricsText(restored.lyricsText);
			if (typeof restored.lyricsFilename === "string")
				setLyricsFilename(restored.lyricsFilename);
		} catch {
			// Ignore malformed cache — fresh state is authoritative.
		}
	}, [draftKey, loadState]);

	const persistDraft = useCallback(
		(next: {
			readonly title: string;
			readonly lyricsMode: LyricsEditMode;
			readonly lyricsText: string;
			readonly lyricsFilename: string | null;
		}) => {
			if (typeof window === "undefined" || draftKey === null) return;
			try {
				window.localStorage.setItem(draftKey, JSON.stringify(next));
			} catch {
				// Persistence is best-effort; visible state stays authoritative.
			}
		},
		[draftKey],
	);

	// Derived state — mirrors the iOS `isDirty` / `canSave` / `saveStateMessage`.
	const trimmedDraftTitle = draftTitle.trim();
	const isDirty = sourceTrack !== null && (
		draftTitle !== sourceTrack.title ||
		draftAlbumFile !== null ||
		removeArtwork ||
		!continentsEqual(draftContinents, originalContinents) ||
		lyricsMode !== (sourceTrack.lyricsText.length > 0 ? "text" : "keep") ||
		lyricsText !== sourceTrack.lyricsText
	);

	const continentsInRange =
		draftContinents.length >= 1 && draftContinents.length <= 3;
	const canSave =
		loadState === "ready" &&
		sourceTrack !== null &&
		!isSaving &&
		isDirty &&
		trimmedDraftTitle.length > 0 &&
		continentsInRange;

	const saveStateMessage = (() => {
		if (isSaving) return "正在保存…";
		if (trimmedDraftTitle.length === 0) return "请填写标题后再保存。";
		if (!continentsInRange) return "请先调整视觉背景数量。";
		if (!isDirty) return "尚未作出修改。";
		return "修改将在保存后应用到这首歌曲。";
	})();

	// Track a per-render snapshot so persisted drafts stay in sync.
	useEffect(() => {
		persistDraft({
			title: draftTitle,
			lyricsMode,
			lyricsText,
			lyricsFilename,
		});
	}, [draftTitle, lyricsMode, lyricsText, lyricsFilename, persistDraft]);

	// ── Action handlers ────────────────────────────────────────────────────

	function handleBack(): void {
		if (isDirty || isSaving) {
			setShowDiscardDialog(true);
			return;
		}
		router.push("/library");
	}

	function handleDiscardConfirm(): void {
		setShowDiscardDialog(false);
		if (typeof window !== "undefined" && draftKey !== null) {
			try {
				window.localStorage.removeItem(draftKey);
			} catch {
				// Best-effort cleanup; navigation proceeds regardless.
			}
		}
		router.push("/library");
	}

	function handleDiscardCancel(): void {
		setShowDiscardDialog(false);
	}

	function openCoverPicker(): void {
		coverInputRef.current?.click();
	}

	function clearDraftAlbum(): void {
		if (draftAlbumURL !== null) URL.revokeObjectURL(draftAlbumURL);
		setDraftAlbumURL(null);
		setDraftAlbumFile(null);
		setRemoveArtwork(true);
	}

	function handleCoverFile(event: ChangeEvent<HTMLInputElement>): void {
		const file = event.target.files?.[0];
		if (file === undefined) return;
		if (!file.type.startsWith("image/")) {
			setError("无法读取该图片，请选择 JPG / PNG 等图片文件。");
			return;
		}
		const url = URL.createObjectURL(file);
		if (draftAlbumURL !== null) URL.revokeObjectURL(draftAlbumURL);
		setDraftAlbumURL(url);
		setDraftAlbumFile(file);
		setRemoveArtwork(false);
		setError(null);
		// Reset input value so the same file can be re-picked.
		event.target.value = "";
	}

	function requestAddContinent(): void {
		if (draftContinents.length >= 3) return;
		setActiveContinentIndex(draftContinents.length);
		continentInputRef.current?.click();
	}

	function replaceContinentAt(index: number): void {
		setActiveContinentIndex(index);
		continentInputRef.current?.click();
	}

	function removeContinentAt(index: number): void {
		setDraftContinents((current) => {
			const next = current.slice();
			next.splice(index, 1);
			return next;
		});
	}

	function moveContinent(index: number, direction: -1 | 1): void {
		const target = index + direction;
		if (target < 0 || target >= draftContinents.length) return;
		setDraftContinents((current) => {
			const next = current.slice();
			const [item] = next.splice(index, 1);
			if (item === undefined) return current;
			next.splice(target, 0, item);
			return next;
		});
	}

	function handleContinentFile(event: ChangeEvent<HTMLInputElement>): void {
		const file = event.target.files?.[0];
		const targetIndex = activeContinentIndex;
		setActiveContinentIndex(null);
		if (file === undefined || targetIndex === null) return;
		const isVideo = file.type.startsWith("video/");
		const isImage = file.type.startsWith("image/");
		if (!isVideo && !isImage) {
			setError("仅支持图片或视频文件。");
			return;
		}
		const url = URL.createObjectURL(file);
		const draft: ContinentDraft = {
			id: makeContinentId(),
			source: isVideo ? "newVideo" : "newImage",
			mediaType: isVideo ? "video" : "image",
			url,
			filename: file.name,
			blob: file,
		};
		setDraftContinents((current) => {
			const next = current.slice();
			if (targetIndex >= next.length) {
				next.push(draft);
			} else {
				next[targetIndex] = draft;
			}
			return next;
		});
		setError(null);
		event.target.value = "";
	}

	function selectLyricsMode(mode: LyricsEditMode): void {
		if (mode === lyricsMode) return;
		setLyricsMode(mode);
		setLyricsFilename(null);
		setLyricsDetection(null);
		if (mode === "file") {
			// Auto-open the file picker, mirroring iOS
			// `resetLyricsStateForMode(.file)` → `showLyricsFilePicker = true`.
			window.setTimeout(() => lyricsInputRef.current?.click(), 0);
		}
		// Keep lyricsText intact so a user toggling `text` ↔ `file` doesn't
		// lose pasted content.
	}

	function handleLyricsFile(event: ChangeEvent<HTMLInputElement>): void {
		const file = event.target.files?.[0];
		if (file === undefined) return;
		setLyricsFilename(file.name);
		file
			.text()
			.then((text) => {
				const bounded = text.slice(0, LYRICS_MAX_CHARS);
				setLyricsDetection(detectLyrics(bounded));
				setLyricsText(bounded);
			})
			.catch(() => {
				setError("无法读取该歌词文件，请确认是 UTF-8 编码的文本。");
			});
		event.target.value = "";
	}

	function handleLyricsTextChange(
		event: ChangeEvent<HTMLTextAreaElement>,
	): void {
		const next = event.target.value.slice(0, LYRICS_MAX_CHARS);
		setLyricsText(next);
		setLyricsDetection(detectLyrics(next));
	}

	async function handleSave(): Promise<void> {
		if (
			!canSave ||
			sourceTrack === null ||
			trackId === null ||
			session.phase !== "user" ||
			session.scope === null
		) return;
		setIsSaving(true);
		setError(null);
		try {
			const nextLyricsText = (() => {
				switch (lyricsMode) {
					case "keep": return sourceTrack.lyricsText;
					case "file":
					case "text": return lyricsText;
					case "autoDetect":
					case "remove": return "";
				}
			})();
			const saveResult = await offlineSocialRepository.saveTrack(
				session.scope,
				trackId,
				{
					title: trimmedDraftTitle,
					continents: draftContinents.map((item) => item.filename),
					cover:
						removeArtwork || (draftAlbumFile === null && sourceTrack.artworkURL === null)
							? "none"
							: "pending",
					lyricsMode: nextLyricsText.length > 0 ? "lyrics" : "instrumental",
				},
				trackRevision,
			);
			if (saveResult.kind === "conflict") {
				setError(saveResult.remoteSummary);
				return;
			}
			if (saveResult.kind !== "saved") {
				setError("当前浏览器无法保存歌曲修改。");
				return;
			}
			const artworkUpdate = removeArtwork
				? { artworkFile: null }
				: draftAlbumFile === null
					? {}
					: { artworkFile: draftAlbumFile };
			await updateLocalTrackAsset(session.scope, trackId, {
				title: trimmedDraftTitle,
				...artworkUpdate,
				lyricsFilename: lyricsMode === "file" ? lyricsFilename : null,
				lyricsText: nextLyricsText,
				continents: draftContinents.map<LocalTrackContinentAsset>((item) => ({
					id: item.id,
					mediaType: item.mediaType,
					filename: item.filename,
					blob: item.blob,
				})),
			});
			setTrackRevision(saveResult.value.revision);
			setNotice("已保存");
			if (typeof window !== "undefined" && draftKey !== null) {
				try {
					window.localStorage.removeItem(draftKey);
				} catch {
				}
			}
			router.push("/library");
		} catch {
			setError("歌曲修改未保存，请检查浏览器存储空间后重试。");
		} finally {
			setIsSaving(false);
		}
	}

	// ── Render ─────────────────────────────────────────────────────────────

	const coverPreviewURL = removeArtwork
		? null
		: (draftAlbumURL ?? sourceTrack?.artworkURL ?? null);
	const heroStatusTitle = isDirty ? "未保存" : "无修改";
	const heroStatusTone = isDirty ? "accent" : "muted";

	if (loadState === "loading") {
		return (
			<section className="native-page track-edit-page" aria-busy="true">
				<NavigationBar title="修改歌曲" backHref="/library" />
				<EmptyState
					title="正在读取歌曲"
					message="正在从此浏览器的作品存储中读取音频与编辑信息。"
					symbol={<ArtistIcon name="music" size={36} />}
				/>
			</section>
		);
	}

	if (loadState === "missing" || sourceTrack === null) {
		return (
			<section className="native-page track-edit-page">
				<NavigationBar title="修改歌曲" backHref="/library" />
				<EmptyState
					title="找不到这首歌曲"
					message="歌曲可能属于另一个账户、已被删除，或尚未从上传页保存到此浏览器。"
					symbol={<ArtistIcon name="music" size={36} />}
					actionLabel="返回作品库"
					onAction={() => router.push("/library")}
				/>
			</section>
		);
	}

	return (
		<section className="native-page track-edit-page" aria-label="修改歌曲">
			<NavigationBar
				title="修改歌曲"
				leading={
					<GlassIconButton
						label="返回作品库"
						type="button"
						onClick={handleBack}
					>
						<ArtistIcon name="back" size={28} strokeWidth={2.2} />
					</GlassIconButton>
				}
				trailing={
					<GlassIconButton
						label="保存修改"
						type="button"
						disabled={!canSave}
						onClick={handleSave}
					>
						<ArtistIcon name="check" size={28} />
					</GlassIconButton>
				}
			/>

			{notice ? (
				<Toast tone="success" onClose={() => setNotice(null)}>
					{notice}
				</Toast>
			) : null}
			{error ? (
				<Toast tone="error" onClose={() => setError(null)}>
					{error}
				</Toast>
			) : null}

			<form
				className="track-edit-form"
				aria-label="修改歌曲表单"
				onSubmit={(event) => {
					event.preventDefault();
					handleSave();
				}}
			>
				{/* ── Hero card ───────────────────────────────────────────────
				    Artwork preview + status + artistAlbum + audio locked
				    message + title field + 3 metrics + change/remove cover. */}
				<GlassCard className="track-edit-hero">
					<div className="track-edit-hero__top">
						<figure
							className="track-edit-artwork"
							style={
								coverPreviewURL === null
									? undefined
									: { backgroundImage: `url(${coverPreviewURL})` }
							}
							aria-label="专辑封面"
						>
							<span className="track-edit-artwork__tag">
								<ArtistIcon name="image" size={12} />
								<span>专辑封面</span>
							</span>
						</figure>
						<div className="track-edit-hero__summary">
							<div className="track-edit-hero__status-row">
								<span
									className={`track-edit-status-badge track-edit-status-badge--${heroStatusTone}`}
								>
									{heroStatusTitle}
								</span>
								{isSaving ? (
									<span className="track-edit-spinner" aria-hidden="true" />
								) : null}
							</div>
							<strong className="track-edit-hero__title">
								{sourceTrack.artist} · {sourceTrack.album}
							</strong>
							<p className="track-edit-hero__hint">
								音频与流媒体文件保持不变，这里只更新这首歌的呈现。
							</p>
						</div>
					</div>

					<GlassTextField
						label="歌曲标题"
						placeholder="给这首歌起个名字"
						leading={<ArtistIcon name="music" size={18} />}
						value={draftTitle}
						onChange={(event) => setDraftTitle(event.target.value)}
						autoComplete="off"
						maxLength={120}
						required
					/>

					<div className="track-edit-metrics">
						<TrackEditMetric
							label="时长"
							icon={<ArtistIcon name="sparkle" size={14} />}
							value={formatDuration(sourceTrack.durationSeconds)}
						/>
						<TrackEditMetric
							label="视觉"
							icon={<ArtistIcon name="image" size={14} />}
							value={`${draftContinents.length}/3`}
						/>
						<TrackEditMetric
							label="歌词"
							icon={<ArtistIcon name="doc" size={14} />}
							value={
								LYRICS_MODES.find((entry) => entry.mode === lyricsMode)
									?.label ?? "—"
							}
						/>
					</div>

					<div className="track-edit-hero__cover-actions">
						<GlassButton
							kind="secondary"
							size="md"
							type="button"
							expands
							disabled={isSaving}
							onClick={openCoverPicker}
							symbol={<ArtistIcon name="camera" size={16} />}
						>
							更换封面
						</GlassButton>
						{draftAlbumURL !== null ? (
							<GlassButton
								kind="secondary"
								size="md"
								type="button"
								expands
								disabled={isSaving}
								onClick={clearDraftAlbum}
								symbol={<ArtistIcon name="back" size={16} />}
							>
								恢复原封面
							</GlassButton>
						) : null}
					</div>

					<input
						ref={coverInputRef}
						type="file"
						accept="image/*"
						className="track-edit-file-input"
						onChange={handleCoverFile}
					/>
				</GlassCard>

				{/* ── Audio locked callout ────────────────────────────────────
				    Mirrors `editTrackAudioLockedMessage` + the read-only audio
				    filename + duration displayed in the hero metrics. */}
				<Callout
					style="info"
					title="音频不可修改"
					message={`原始文件 ${sourceTrack.audioFilename} · 时长 ${formatDuration(sourceTrack.durationSeconds)}。`}
					symbol={<ArtistIcon name="music" size={18} />}
				/>

				{/* ── Continents section ─────────────────────────────────────
				    Subtitle + list of continent cards (with replace / remove /
				    up / down) + add card when count < 3. */}
				<section className="track-edit-section" aria-label="视觉背景">
					<header className="track-edit-section__head">
						<h2>视觉背景</h2>
						<span>{draftContinents.length}/3</span>
					</header>
					<p className="track-edit-section__subtitle">
						最多添加 3 个静态或动态背景，按上下按钮调整顺序。
					</p>
					{draftContinents.length <= 3 ? null : (
						<p className="track-edit-section__subtitle" role="alert">
							添加至少 1 个、最多 3 个背景。
						</p>
					)}

					{draftContinents.length === 0 ? (
						<GlassCard className="track-edit-empty">
							<EmptyState
								title="还没有视觉背景"
								message="添加至少 1 个、最多 3 个背景，保存后会替换原有视觉。"
								symbol={<ArtistIcon name="image" size={36} />}
								actionLabel="添加 Continent"
								actionSymbol={<ArtistIcon name="plus" size={16} />}
								onAction={requestAddContinent}
							/>
						</GlassCard>
					) : (
						<ol className="track-edit-continent-list">
							{draftContinents.map((continent, index) => (
								<ContinentRow
									key={continent.id}
									continent={continent}
									indexLabel={`Continent ${index + 1}`}
									canMoveUp={index > 0}
									canMoveDown={index < draftContinents.length - 1}
									onMoveUp={() => moveContinent(index, -1)}
									onMoveDown={() => moveContinent(index, 1)}
									onReplace={() => replaceContinentAt(index)}
									onRemove={() => removeContinentAt(index)}
									disabled={isSaving}
								/>
							))}
						</ol>
					)}

					{draftContinents.length > 0 && draftContinents.length < 3 ? (
						<GlassCard className="track-edit-add-card">
							<span className="track-edit-add-card__icon" aria-hidden="true">
								<ArtistIcon name="plus" size={18} />
							</span>
							<span className="track-edit-add-card__copy">
								<strong>添加 Continent</strong>
								<small>在不离开编辑器的情况下添加另一个背景。</small>
							</span>
							<GlassButton
								kind="secondary"
								size="sm"
								type="button"
								onClick={requestAddContinent}
								disabled={isSaving}
								symbol={<ArtistIcon name="plus" size={14} />}
							>
								添加
							</GlassButton>
						</GlassCard>
					) : null}

					<input
						ref={continentInputRef}
						type="file"
						accept="image/*,video/*"
						className="track-edit-file-input"
						onChange={handleContinentFile}
					/>
				</section>

				{/* ── Lyrics section ─────────────────────────────────────────
				    Subtitle + 5-mode selector (vertical list, mirrors
				    TrackEditLyricsModeSelector) + mode-specific body. */}
				<section className="track-edit-section" aria-label="歌词">
					<header className="track-edit-section__head">
						<h2>歌词</h2>
					</header>
					<p className="track-edit-section__subtitle">
						选择保存后歌词的处理方式，并预览识别结果。
					</p>

					<GlassCard className="track-edit-lyrics-mode-list">
						{LYRICS_MODES.map((entry, index) => {
							const selected = entry.mode === lyricsMode;
							return (
								<div key={entry.mode} className="track-edit-lyrics-mode">
									<button
										type="button"
										className="track-edit-lyrics-mode__row"
										aria-pressed={selected}
										onClick={() => selectLyricsMode(entry.mode)}
										disabled={isSaving}
									>
										<span
											className={`track-edit-lyrics-mode__icon${selected ? " is-selected" : ""}`}
											aria-hidden="true"
										>
											<ArtistIcon name={entry.icon} size={18} />
										</span>
										<span className="track-edit-lyrics-mode__copy">
											<strong>{entry.label}</strong>
											<small>{entry.description}</small>
										</span>
										<span
											className={`track-edit-lyrics-mode__check${selected ? " is-selected" : ""}`}
											aria-hidden="true"
										>
											<ArtistIcon
												name={selected ? "check" : "close"}
												size={18}
												strokeWidth={selected ? 2.4 : 1.6}
											/>
										</span>
									</button>
									{index < LYRICS_MODES.length - 1 ? (
										<span
											className="track-edit-lyrics-mode__divider"
											aria-hidden="true"
										/>
									) : null}
								</div>
							);
						})}
					</GlassCard>

					<LyricsEditorBody
						mode={lyricsMode}
						lyricsText={lyricsText}
						lyricsFilename={lyricsFilename}
						lyricsDetection={lyricsDetection}
						disabled={isSaving}
						onTextChange={handleLyricsTextChange}
						onOpenFilePicker={() => lyricsInputRef.current?.click()}
					/>

					<input
						ref={lyricsInputRef}
						type="file"
						accept=".lrc,.txt,.srt,.vtt,text/plain"
						className="track-edit-file-input"
						onChange={handleLyricsFile}
					/>
				</section>

				<button
					type="submit"
					aria-hidden="true"
					tabIndex={-1}
					style={visuallyHidden}
				/>
			</form>

			{/* ── Sticky save bar ───────────────────────────────────────────
			    Mirrors `TrackEditSaveBar` (`safeAreaInset(edge: .bottom)`). */}
			<section className="track-edit-save-bar" aria-label="保存操作">
				<p className="track-edit-save-bar__message">{saveStateMessage}</p>
				<GlassButton
					kind="primary"
					size="lg"
					type="button"
					isEnabled={canSave}
					isLoading={isSaving}
					onClick={handleSave}
					symbol={<ArtistIcon name="check" size={16} />}
				>
					保存
				</GlassButton>
			</section>

			{/* ── Discard confirmation dialog ───────────────────────────────
			    Mirrors iOS `.confirmationDialog(Text(.Make.discardUnsavedChangesTitle))`. */}
			<dialog
				className="track-edit-dialog"
				open={showDiscardDialog}
				aria-labelledby="track-edit-discard-title"
			>
				<div className="track-edit-dialog__body">
					<h2 id="track-edit-discard-title">放弃改动？</h2>
					<p>退出后未保存的修改将丢失，且无法恢复。</p>
					<div className="track-edit-dialog__actions">
						<GlassButton
							kind="destructive"
							type="button"
							onClick={handleDiscardConfirm}
						>
							放弃改动
						</GlassButton>
						<GlassButton
							kind="secondary"
							type="button"
							onClick={handleDiscardCancel}
						>
							继续编辑
						</GlassButton>
					</div>
				</div>
			</dialog>
		</section>
	);
}

// ─────────────────────────────────────────────────────────────────────────────
// TrackEditMetric — small metric tile (label + value) used in the hero card.
// Mirrors iOS `TrackEditMetric`.
// ─────────────────────────────────────────────────────────────────────────────

function TrackEditMetric({
	label,
	icon,
	value,
}: Readonly<{ label: string; icon: ReactNode; value: string }>) {
	return (
		<div className="track-edit-metric">
			<span className="track-edit-metric__label">
				{icon}
				<span>{label}</span>
			</span>
			<strong className="track-edit-metric__value">{value}</strong>
		</div>
	);
}

// ─────────────────────────────────────────────────────────────────────────────
// ContinentRow — mirrors `ContinentDraftRow`. Thumbnail + index label + media
// tag + source detail + replace / remove / move controls.
// ─────────────────────────────────────────────────────────────────────────────

interface ContinentRowProps {
	readonly continent: ContinentDraft;
	readonly indexLabel: string;
	readonly canMoveUp: boolean;
	readonly canMoveDown: boolean;
	readonly onMoveUp: () => void;
	readonly onMoveDown: () => void;
	readonly onReplace: () => void;
	readonly onRemove: () => void;
	readonly disabled: boolean;
}

function ContinentRow({
	continent,
	indexLabel,
	canMoveUp,
	canMoveDown,
	onMoveUp,
	onMoveDown,
	onReplace,
	onRemove,
	disabled,
}: Readonly<ContinentRowProps>) {
	const mediaTag =
		continent.source === "existing"
			? continent.mediaType === "video"
				? "视频 · 已保存"
				: "图片 · 已保存"
			: continent.source === "newVideo"
				? "视频 · 待保存"
				: "图片 · 待保存";
	const sourceDetail =
		continent.source === "existing"
			? "已存在于曲目中。调整顺序仅改变播放顺序。"
			: continent.source === "newVideo"
				? "新动态背景，等待保存后生效。"
				: "新静态背景，等待保存后生效。";
	const trailingIcon = continent.mediaType === "video" ? "play" : "image";
	return (
		<li className="track-edit-continent">
			<div className="track-edit-continent__thumb">
				{continent.mediaType === "image" ? (
					<ArtworkThumbnail src={continent.url} alt="" size={84} />
				) : (
					<span className="track-edit-continent__video-thumb">
						<ArtistIcon name="play" size={22} />
					</span>
				)}
			</div>
			<div className="track-edit-continent__copy">
				<span className="track-edit-status-badge track-edit-status-badge--muted">
					{indexLabel}
				</span>
				<strong>{mediaTag}</strong>
				<small>{sourceDetail}</small>
			</div>
			<div className="track-edit-continent__actions">
				<div className="track-edit-continent__move">
					<GlassIconButton
						label="上移"
						type="button"
						disabled={disabled || !canMoveUp}
						onClick={onMoveUp}
					>
						<ArtistIcon
							name="chevron"
							size={18}
							style={{ transform: "rotate(-90deg)" }}
						/>
					</GlassIconButton>
					<GlassIconButton
						label="下移"
						type="button"
						disabled={disabled || !canMoveDown}
						onClick={onMoveDown}
					>
						<ArtistIcon
							name="chevron"
							size={18}
							style={{ transform: "rotate(90deg)" }}
						/>
					</GlassIconButton>
				</div>
				<GlassIconButton
					label="替换"
					type="button"
					disabled={disabled}
					onClick={onReplace}
				>
					<ArtistIcon name={trailingIcon} size={18} />
				</GlassIconButton>
				<GlassIconButton
					label="删除"
					type="button"
					kind="destructive"
					disabled={disabled}
					onClick={onRemove}
				>
					<ArtistIcon name="trash" size={18} />
				</GlassIconButton>
			</div>
		</li>
	);
}

// ─────────────────────────────────────────────────────────────────────────────
// LyricsEditorBody — mirrors `lyricsEditorCard`. keep / autoDetect / remove
// render a message card; file renders a file picker row; text renders the
// multi-line editor with a counter and detection preview.
// ─────────────────────────────────────────────────────────────────────────────

interface LyricsEditorBodyProps {
	readonly mode: LyricsEditMode;
	readonly lyricsText: string;
	readonly lyricsFilename: string | null;
	readonly lyricsDetection: LyricsDetection | null;
	readonly disabled: boolean;
	readonly onTextChange: (event: ChangeEvent<HTMLTextAreaElement>) => void;
	readonly onOpenFilePicker: () => void;
}

function LyricsEditorBody({
	mode,
	lyricsText,
	lyricsFilename,
	lyricsDetection,
	disabled,
	onTextChange,
	onOpenFilePicker,
}: Readonly<LyricsEditorBodyProps>) {
	const descriptor = LYRICS_MODES.find((entry) => entry.mode === mode);

	if (mode === "keep" || mode === "autoDetect" || mode === "remove") {
		return (
			<GlassCard className="track-edit-message-card">
				<strong>{descriptor?.label ?? ""}</strong>
				<p>{descriptor?.message ?? ""}</p>
			</GlassCard>
		);
	}

	if (mode === "file") {
		return (
			<GlassCard className="track-edit-lyrics-file">
				<button
					type="button"
					className="track-edit-lyrics-picker"
					onClick={onOpenFilePicker}
					disabled={disabled}
				>
					<span className="track-edit-lyrics-picker__icon" aria-hidden="true">
						<ArtistIcon name="doc" size={22} />
					</span>
					<span className="track-edit-lyrics-picker__copy">
						<strong>{lyricsFilename ?? "选择歌词文件"}</strong>
						<small>
							{lyricsFilename !== null
								? "已选择 · 点击替换"
								: "支持 .lrc / .txt / .srt / .vtt"}
						</small>
					</span>
					<span
						className="track-edit-lyrics-picker__trailing"
						aria-hidden="true"
					>
						<ArtistIcon name="chevron" size={18} />
					</span>
				</button>
				<LyricsDetectionPreview detection={lyricsDetection} />
			</GlassCard>
		);
	}

	return (
		<GlassCard className="track-edit-lyrics-text">
			<GlassTextEditor
				label="歌词文本"
				placeholder="粘贴或输入歌词，可包含 [mm:ss.xx] 时间标签…"
				value={lyricsText}
				onChange={onTextChange}
				disabled={disabled}
				maxLength={LYRICS_MAX_CHARS}
				counter={`${lyricsText.length}/${LYRICS_MAX_CHARS}`}
			/>
			<div className="track-edit-lyrics-text__hint">
				<span>支持 LRC 时间标签；纯文本将自动对齐。</span>
				<span>
					{lyricsText.length}/{LYRICS_MAX_CHARS}
				</span>
			</div>
			<LyricsDetectionPreview detection={lyricsDetection} />
		</GlassCard>
	);
}

// ─────────────────────────────────────────────────────────────────────────────
// LyricsDetectionPreview — mirrors `lyricsDetectionView`. Shows an info
// callout with the detected kind + preview line when present.
// ─────────────────────────────────────────────────────────────────────────────

function LyricsDetectionPreview({
	detection,
}: Readonly<{ detection: LyricsDetection | null }>) {
	if (detection === null) return null;
	const isSynced = detection.kind === "synced";
	return (
		<Callout
			style="info"
			title={isSynced ? "识别为同步歌词" : "识别为纯文本歌词"}
			message={`预览：${detection.preview}`}
			symbol={<ArtistIcon name="sparkle" size={16} />}
		/>
	);
}

const visuallyHidden = {
	position: "absolute",
	width: "1px",
	height: "1px",
	padding: 0,
	margin: "-1px",
	overflow: "hidden",
	clip: "rect(0,0,0,0)",
	whiteSpace: "nowrap",
	border: 0,
} as const;
